<template>
  <article class="mx-auto max-w-5xl px-4 sm:px-6 py-8">
    <!-- Breadcrumb -->
    <nav class="mb-6 text-sm text-gray-500">
      <ol class="flex flex-wrap items-center gap-2">
        <li><NuxtLink to="/" class="hover:text-gray-700 transition">Home</NuxtLink></li>
        <li aria-hidden="true">/</li>
        <li>
          <NuxtLink to="/help" class="hover:text-gray-700 transition"
            >Help Center</NuxtLink
          >
        </li>
        <li aria-hidden="true">/</li>
        <li class="text-gray-700 truncate max-w-[50ch]">{{ article?.title || "…" }}</li>
      </ol>
    </nav>

    <!-- Header -->
    <header class="mb-8">
      <h1 class="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
        {{ article?.title || "Loading…" }}
      </h1>

      <div class="mt-3 flex flex-wrap items-center gap-3 text-sm text-gray-500">
        <span v-if="article?.createdAt"
          >Published: {{ formatDate(article.createdAt) }}</span
        >
        <span v-if="article?.updatedAt"
          >· Updated: {{ formatDate(article.updatedAt) }}</span
        >

        <button
          @click="copy(pageUrl)"
          class="inline-flex items-center gap-1 rounded-full border border-gray-300 px-3 py-1 hover:bg-gray-50 active:scale-[0.98] transition"
          :aria-label="copied ? 'Link copied' : 'Copy link'"
        >
          <span v-if="!copied">Skopiuj link</span>
          <span v-else>Skopiowano!</span>
        </button>
      </div>

      <!-- Optional cover: gif/video/image via frontmatter `cover` -->
      <div
        v-if="article?.cover"
        class="mt-6 overflow-hidden rounded-2xl bg-gray-100 shadow-sm"
      >
        <video
          v-if="isVideo(article.cover)"
          :src="article.cover"
          autoplay
          muted
          loop
          playsinline
          class="w-full"
        />
        <img v-else :src="article.cover" alt="" class="w-full" />
      </div>
    </header>

    <!-- Content + TOC -->
    <div class="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-10">
      <div class="prose prose-neutral lg:prose-xl max-w-none">
        <!-- Main markdown body -->
        <ContentRenderer v-if="article" :value="article" />
      </div>

      <aside class="lg:sticky lg:top-20 lg:h-fit">
        <div class="rounded-2xl border border-gray-200 p-4">
          <p class="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Na tej stronie
          </p>

          <nav v-if="flatToc.length" class="space-y-2">
            <a
              v-for="(h, i) in flatToc"
              :key="i"
              class="block text-sm leading-5 text-gray-700 hover:text-gray-900 transition"
              :class="h.depth === 3 ? 'pl-4' : ''"
              :href="`#${h.id}`"
            >
              {{ h.text }}
            </a>
          </nav>
          <p v-else class="text-sm text-gray-500">—</p>
        </div>
      </aside>
    </div>
  </article>
</template>

<script setup>
/**
 * Data loading (your provided pattern)
 */
const route = useRoute();
console.log(route.path, "route path");
const { data: article } = await useAsyncData(route.path, () => {
  return queryCollection("pomoc").path(route.path).first();
});

/**
 * Copy link (no window/document direct usage)
 */
const { copy, copied } = useClipboard();
const reqUrl = useRequestURL();
const pageUrl = computed(() => reqUrl.href);

/**
 * Dates
 */
function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * Cover type helper
 */
function isVideo(src) {
  return typeof src === "string" && /\.(mp4|webm|mov|m4v)$/i.test(src);
}

/**
 * Table of contents from @nuxt/content (no DOM scanning)
 * Uses document.body.toc.links if available
 */
const toc = computed(() => article.value?.body?.toc?.links || []);
const flatToc = computed(() => {
  const out = [];
  const walk = (links = []) => {
    for (const l of links) {
      out.push({ id: l.id, text: l.text, depth: l.depth });
      if (l.children?.length) walk(l.children);
    }
  };
  walk(toc.value);
  return out;
});
</script>

<style scoped></style>
